import { useTheme } from "@emotion/react";
import { Box, ButtonBase, InputBase } from "@mui/material";
import cx from 'classnames';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup.jsx";
import Shbutton from "../../../../components/theme/buttons/Shbutton.jsx";
import { DropdownBase, ShDropdownSearchQuery } from "../../../../components/theme/dropdowns/Dropdown.jsx";
import SelectGroup from "../../../../components/theme/Select/SelectGroup.jsx";
import Heading from "../../../../components/theme/text/Heading.jsx";
import Subheading from "../../../../components/theme/text/Subheading.jsx";
import Text from "../../../../components/theme/text/Text.jsx";
import { setWorkSpace } from "../../../../newapi/global/settingsSlice.js";
import { useGetReturnLineToProcessByIdMutation, useGetReturnReasonsForProcessSelectQuery, useUpdateReturnLineToProcessByIdMutation, useGetReturnLinePhotosQuery, useUploadReturnLinePhotoMutation, useDeleteReturnLinePhotoMutation } from "../../../../newapi/return/returnsSlice.js";
import LinePhotos from "../../../../components/theme/photos/LinePhotos.jsx";
import MLinePhotoCapture from "../../../../components/global/Modals/MLinePhotoCapture.jsx";
import MPhotoViewer from "../../../../components/global/Modals/MPhotoViewer.jsx";
import MReturnLocationDuplicate from "../../../../components/global/Modals/MReturnLocationDuplicate.jsx";
import EMReturnProcessing from "../../../../components/global/ModalsPopover/explainers/returns/EMReturnProcessing.jsx";
import { useFindWorkspacesSelectQuery } from "../../../../newapi/warehouse/workspaceSlice.js";
import { themeSettings, tokens } from "../../../../theme.js";
import { general_states, photo_source, warehouselocationtype_type } from "../../../../utils/staticEnums.js";
import ShValidatedNumberInput from "../../../../components/theme/inputs/ShValidatedNumberInput.jsx";
import ShSwitch from "../../../../components/theme/inputs/ShSwitch.jsx";
import { getWarehouseLocationLabelData } from "../../../../utils/labelColorText.js";
import ShLabel from "../../../../components/theme/label/ShLabel.jsx";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import SidepanelLoader from "../../../../components/theme/sidepanel2/SidepanelLoader.js";
import { useGetSuggestedLocationsQuery } from "../../../../newapi/warehouse/productSlice.js";
import { useGetWarehouseLocationSelectQuery } from "../../../../newapi/warehouse/warehouselocationSlice.js";
import { ReactComponent as VirtualProduct } from '../../../../styles/svg/virtuaproduct.svg';
import { showToastMessageRequestError } from "../../../../utils/toasts.js";
import { stockhubReturnPreCode } from "../../../../utils/constants.js";
// import SPStockDetails from "../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails.jsx";

const SPStockDetails = SidepanelLoader(() => import("../../../../components/global/Sidepanels/SpStockDetails/SPStockDetails"));

const ReturnsToProcessView = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    const dispatch = useDispatch();

    const inputRef = useRef(null);
    const countRef = useRef(null);
    const serialNumberRef = useRef(null);
    const [getReturnLine, {isLoading: getReturnLineLoading}] = useGetReturnLineToProcessByIdMutation();
    const [updateReturnLine, {isLoading:updatingReturnLine}] = useUpdateReturnLineToProcessByIdMutation();

    const buttonRef = useRef(null);

    const [returnLineToProcess, setReturnLineToProcess] = useState(null);
    const [splitReturnLines, setSplitReturnLines] = useState(null);
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const {data: returnReasons, loading: returnReasonsLoading, fetching: returnReasonsFetching} = useGetReturnReasonsForProcessSelectQuery(props.incData.relationId, {skip: !props.incData.relationId || returnLineToProcess === null});

    const {data: suggestedLocations, isLoading: suggestedLocationsLoading} = useGetSuggestedLocationsQuery(returnLineToProcess?.product?.id, {
        skip: !returnLineToProcess?.product?.id && (!returnLineToProcess?.hasReturnLocation),
    })
   
    const [productsSpIsExtended, setProductsSpIsExtended] = useState(false);
    const [spProductLoading, setSpProductLoading] = useState();

    const [photoModalOpen, setPhotoModalOpen] = useState(false);
    const [photoModalMode, setPhotoModalMode] = useState('pc');
    const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

    const { data: photosData, refetch: refetchPhotos, isLoading: photosLoading, isFetching: photosFetching } = useGetReturnLinePhotosQuery(
        { returnId: props.incData.id, returnLineId: returnLineToProcess?.id },
        { skip: !returnLineToProcess?.id }
    );
    const [uploadPhoto, { isLoading: isUploadingPhoto }] = useUploadReturnLinePhotoMutation();
    const [deletePhoto, { isLoading: isDeletingPhoto }] = useDeleteReturnLinePhotoMutation();

    const handleUploadPhoto = (file) => uploadPhoto({
        returnId: props.incData.id,
        returnLineId: returnLineToProcess.id,
        file,
    }).unwrap();

    const handleDeletePhotoById = (photoId) => deletePhoto({
        returnId: props.incData.id,
        returnLineId: returnLineToProcess.id,
        photoId,
    }).unwrap();

    const handleOpenPhotoModal = (mode) => {
        setPhotoModalMode(mode);
        setPhotoModalOpen(true);
    };

    const handlePhotoClick = (index) => {
        setCurrentPhotoIndex(index);
        setPhotoViewerOpen(true);
    };

    const handleDeletePhoto = async (photoId) => {
        // Consumer photos are evidence and can't be deleted from the warehouse.
        if ((photosData || []).find(photo => photo.id === photoId)?.source === photo_source.CONSUMER) return;
        try {
            await deletePhoto({
                returnId: props.incData.id,
                returnLineId: returnLineToProcess.id,
                photoId,
            }).unwrap();
            refetchPhotos();
        } catch (err) {
            toast.error(t("error"));
        }
    };

    const [extendedReturnReasons, setExtendedReturnReasons] = useState([]);
    useEffect(() => {
        if (!returnReasonsLoading && !returnReasonsFetching && returnReasons) {
            const customOptionId = 'custom-other';
    
            // Check if it's already added
            const alreadyHasCustom = returnReasons.some(reason => reason.id === customOptionId);
    
            const extended = alreadyHasCustom
                ? returnReasons
                : [
                    ...returnReasons,
                    {
                        id: customOptionId,
                        description: t("specify_return_reason")
                    }
                ];
    
            setExtendedReturnReasons(extended);
        }
    }, [returnReasons, returnReasonsLoading, returnReasonsFetching]);

    // console.log(props.incData)
    const handleKeyDown = async (e) => {
        if (e.key === 'Enter') {
            let returnLine = props.incData.returnlines.find(line => {
                return line.state === general_states.ACTIVE && line.product.barcodes.includes(e.target.value)
            });
            if(typeof returnLine === "undefined") {
                props.setHighlightColor("red");
                toast.error(t("cant_find_product_with_this_barcode"), {
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                e.target.value = "";
                return;
            }
            getReturnLine({
                returnId: props.incData.id,
                returnLineId:returnLine.id,
            }).unwrap()
            
            .then(res => {
                
                props.setHighlightColor("green");
                setReturnLineToProcess({
                    ...res,
                    internalReason:"",
                    internalReasonIndex:-1,
                    internalRemark: "",
                    acceptedCount: props.incData.rejectAll ? 0 : 0,
                    rejectedCount: props.incData.rejectAll ? res.amount : 0,
                    notEverythingReceived: false,
                    ...(res.product?.serialNumberRequired && { serialnumbers: [] }),
                });



            })
            .catch(err => {
                props.setHighlightColor("red");
            })

            e.target.value = "";
        }
    }

    const handleOpen = (returnLineId) => {
        if(getReturnLineLoading) return;

        let returnLine = props.incData.returnlines.find(line => line.id === returnLineId);
        if(typeof returnLine === "undefined") {
            props.setHighlightColor("red");
            toast.error(t("cant_find_product_with_this_barcode"), {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            return;
        }
        getReturnLine({
            returnId: props.incData.id,
            returnLineId:returnLine.id,
        }).unwrap()
        
        .then(res => {
            props.setHighlightColor("green");
            setReturnLineToProcess({
                ...res,
                internalReason:"",
                internalReasonIndex:-1,
                internalRemark: "",
                acceptedCount: (props.incData.rejectAll || res.product.rejectOnReturn) ? 0 : 0,
                rejectedCount: (props.incData.rejectAll || res.product.rejectOnReturn)? res.amount : 0,
                notEverythingReceived: false,
                ...(res.product?.serialNumberRequired && { serialnumbers: [] }),
            });


        })
        .catch(err => {
            props.setHighlightColor("red");
        })
    }

    const handleSerialnumber = (e) => {
        if (e.key === 'Enter') {
            const inputValue = e.target.value.trim();
            if (inputValue.length === 0) return;

            const alreadyScanned = (returnLineToProcess?.serialnumbers ?? []).some(sn => sn.barcode === inputValue);
            if (alreadyScanned) {
                props.setHighlightColor("orange");
                if (serialNumberRef.current) serialNumberRef.current.focus();
                e.target.value = "";
                return;
            }

            setReturnLineToProcess(line => {
                const updated = [...(line.serialnumbers ?? []), { barcode: inputValue }];
                const accepted = updated.length;
                return {
                    ...line,
                    serialnumbers: updated,
                    acceptedCount: accepted,
                    rejectedCount: Math.max(0, line.amount - accepted),
                };
            });

            props.setHighlightColor("green");
            if (serialNumberRef.current) serialNumberRef.current.focus();
            e.target.value = "";
        }
    }

    const removeSerialnumber = (barcode) => {
        setReturnLineToProcess(line => {
            const updated = (line.serialnumbers ?? []).filter(sn => sn.barcode !== barcode);
            const accepted = updated.length;
            return {
                ...line,
                serialnumbers: updated,
                acceptedCount: accepted,
                rejectedCount: Math.max(0, line.amount - accepted),
            };
        });
    }


    // const updateSplitLineAcceptanceState = (uniqueId, newState) => {
    //     setSplitReturnLines(prevLines => {
    //         const updatedLines = prevLines.map(line =>
    //             line.uniqueId === uniqueId
    //                 ? { ...line, acceptance_state: newState }
    //                 : line
    //         );
    
    //         // Recalculate counts
    //         const accepted = updatedLines.filter(l => l.acceptance_state === true).length;
    //         const rejected = updatedLines.filter(l => l.acceptance_state === false).length;
    
    //         // Update the counts in returnLineToProcess
    //         setReturnLineToProcess(prev => ({
    //             ...prev,
    //             acceptedCount: accepted,
    //             rejectedCount: rejected
    //         }));
    
    //         return updatedLines;
    //     });
    // };

    const handleSave = async () => {
        if(updatingReturnLine) return;
        if(!props.incData.rejectAll && !returnLineToProcess.product.rejectOnReturn){
            if(!returnLineToProcess.notEverythingReceived && (returnLineToProcess.acceptedCount + returnLineToProcess.rejectedCount) < returnLineToProcess.amount) {
                props.setHighlightColor("red");
                toast.error(t("unprocessed_return_lines"), {
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                return;
            }
            if(returnLineToProcess.product?.serialNumberRequired && returnLineToProcess.acceptedCount !== (returnLineToProcess.serialnumbers?.length ?? 0)) {
                props.setHighlightColor("red");
                toast.error(t("serial_numbers_insufficient"), {
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                return;
            }
            if(returnLineToProcess.acceptedCount > 0 && !returnLineToProcess.hasReturnLocation && !returnLineToProcess.warehouseLocationId) {
                props.setHighlightColor("red");
                toast.error(t("choose_a_location_to_book_the_items_onto"), {
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                return;
            }
            // if(returnLineToProcess.internalReasonIndex === -1) {
            //     props.setHighlightColor("red");

            //     toast.error(t("no_return_reason_given"), {
            //         position: "bottom-left",
            //         autoClose: 5000,
            //         hideProgressBar: false,
            //         closeOnClick: true,
            //         pauseOnHover: true,
            //         draggable: true,
            //         progress: undefined,
            //         theme: "light",
            //     });
            //     props.setHighlightColor("red");
            //     return;
            // }

            if((returnLineToProcess.internalReasonIndex + 1) === extendedReturnReasons.length && returnLineToProcess.internalReason.length === 0) {
                props.setHighlightColor("red");

                toast.error(t("no_return_reason_given"), {
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
                props.setHighlightColor("red");
                return;
            }
        }
        if(!props.incData.rejectAll && !returnLineToProcess.product.rejectOnReturn && returnLineToProcess.hasDuplicate && (returnLineToProcess.acceptedCount > 0)) {
            setDuplicateModalOpen(true);
        } else {
            doSave();
        }
    }

    const doSave = async () => {

        updateReturnLine({
            returnId: props.incData.id,
            returnLineId: returnLineToProcess.id,
            internalRemark: returnLineToProcess.internalRemark,
            acceptedCount: (props.incData.rejectAll || returnLineToProcess.product.rejectOnReturn) ? 0 : returnLineToProcess.acceptedCount,
            rejectedCount: (props.incData.rejectAll || returnLineToProcess.product.rejectOnReturn) ? returnLineToProcess.amount : returnLineToProcess.rejectedCount,
            internalReason: (props.incData.rejectAll || returnLineToProcess.product.rejectOnReturn) ?
                            "" :
                            (returnLineToProcess.internalReasonIndex + 1) === extendedReturnReasons.length ?
                                returnLineToProcess.internalReason :
                                extendedReturnReasons[returnLineToProcess.internalReasonIndex]?.description,

            ...(!returnLineToProcess.hasReturnLocation && (!props.incData.rejectAll || !returnLineToProcess.product.rejectOnReturn) && {warehouseLocationId:returnLineToProcess.warehouseLocationId} ),
            serialNumbers: (returnLineToProcess.serialnumbers ?? []).map(sn => sn.barcode),
            notEverythingReceived: (returnLineToProcess.acceptedCount + returnLineToProcess.rejectedCount) < returnLineToProcess.amount && returnLineToProcess.notEverythingReceived === true,
            overcount: (returnLineToProcess.acceptedCount + returnLineToProcess.rejectedCount) > returnLineToProcess.amount
        })
        .unwrap()
        .then(res => {
            setReturnLineToProcess(null);
            setSplitReturnLines(null);
            props.getReturn(props.incData.id);
        })
        .catch(err => {
            showToastMessageRequestError(t("create_failed"), t, err);

            toast.error(err.data.error, {
                position: "bottom-left",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light",
            });
            props.setHighlightColor("red");
        })
    }
    // const activeReturnLines = props.incData.returnlines?.filter(
    //     (line) => line.state === general_states.ACTIVE
    // );
    // const completedReturnLines = props.incData.returnlines?.filter(
    //     (line) => line.state !== general_states.ACTIVE
    // );

    const grouped = {};
    const singles = [];

    (props.incData.returnlines ?? []).forEach(line => {
        if (!line.parentProduct) {
            // standalone line
            singles.push({
            parentProduct: null,
            lines: [line],
            state: line.state,
            });
            return;
        }

        const key = line.parentProduct.id;

        if (!grouped[key]) {
            grouped[key] = {
                parentProduct: line.parentProduct,
                lines: [],
                state: null,
            };
        }

        grouped[key].lines.push(line);
    });
    const parentGroups = Object.values(grouped).map(group => {
        const hasActive = group.lines.some(
            line => line.state === general_states.ACTIVE
        );

        return {
            ...group,
            state: hasActive
            ? general_states.ACTIVE
            : general_states.COMPLETED,
        };
    });
    const combinedReturnLines = [
        ...parentGroups,
        ...singles,
    ];
    const activeReturnLines = combinedReturnLines.filter(
        item => item.state === general_states.ACTIVE
    );

    const completedReturnLines = combinedReturnLines.filter(
        item => item.state !== general_states.ACTIVE
    );

    // Once the counts already account for the whole line (or more) nothing is
    // missing, so "not everything received" can't apply and the switch is locked.
    const everythingAccountedFor = returnLineToProcess
        ? (returnLineToProcess.acceptedCount + returnLineToProcess.rejectedCount) >= returnLineToProcess.amount
        : false;

    return(
        <Box
            sx={{
                flex:1,
                marginBottom:3,
                position:"relative",
                display:"flex",
                overflow:"hidden",
                "@media screen and (max-width: 1080px)" : {
                    flexDirection:'column'
                }
            }}
        >
            <Box 
                sx={{
                    "@media screen and (max-width: 1080px)" : {
                        display:'flex'
                    }
                }}
            >
                <ButtonBase
                    onClick={() => {
                        // if(inboundLineToProcess !== null) {
                        //     setInboundLineToProcess(null);
                        // }  else {
                            props.setShowReturnToProcess(false);
                        // }
                    }}
                    sx={{
                        ml:"-16px",
                        fontWeight:"bold",
                        p:2,
                        color:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]
                    }}
                >
                    {t("close")}
                    {/* {inboundLineToProcess !== null ? t("go_back") : t("close")} */}
                </ButtonBase>

                <Box
                    sx={{
                        display:"flex",
                        // overflow:"hidden",
                    }}
                >
                    <Box
                        sx={{
                            flexShrink:0,
                            mt:1,
                            flex:1,
                            width:370,
                            "@media screen and (max-width: 1366px)" : {
                                width:300,
                            }
                        }}
                    >
                       
                     


                        <Heading>{t("to_process")}</Heading>
                        <Box>
                            <Text>{props.incData.reference}</Text>
                            <Text sx={{marginLeft:1,}} light italic>{props.incData.order?.externalId}</Text>
                        </Box>
                        {props.incData.remark &&
                            <Box
                                sx={{
                                    borderRadius:4,
                                    mt:1.5,
                                    mb:1.5,
                                    display:'flex',
                                    alignItems:"center",
                                    flexDirection:'column',
                                    px:2,
                                    py:2,
                                    gap:1,
                                    background:theme.palette.mode === "dark" ? colors.red[100] : colors.red[100],
                                }}
                            >
                                <Box
                                    sx={{
                                        width:1,
                                        px:1.5,
                                        height:24,
                                        borderRadius:3,
                                        background: colors.red[400],
                                        color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                                        textAlign:"center",
                                    }}
                                >
                                    <Text 
                                        semibold 
                                        color={theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"]}
                                    >
                                        {t("client_remark")}
                                    </Text>
                                </Box>
                                <Text variant={"red"}>{props.incData.remark}</Text>
        
                            </Box>
                        }
                        {activeReturnLines?.map((line,index) => {

                            // return null;
                            if(line.parentProduct) {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                            p:1,
                                            mt:1.5,
                                            borderRadius:4,
                                        }}
                                    >

                                        <Box
                                            sx={{
                                                display:"flex",
                                                gap:3,
                                                justifyContent:'space-between',
                                                p:1,
                                                pb:0,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    flexDirection:'column',

                                                }}
                                            >

                                                <Text bold>{line.parentProduct.description}</Text>
                                                <Text>{line.parentProduct.sku}</Text>
                                            </Box>
                                            <Box
                                                sx={{
                                                    background:theme.palette.mode === "dark" ? colors.orange[100] : "#FEF0E5",
                                                    color: theme.palette.mode === "dark" ? colors.orange[400] : "#F56900",
                                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.orange[400] : "#F56900"}`,
                                                    height:32,
                                                    lineHeight:"32px",
                                                    display:'flex',
                                                    alignItems:'center',
                                                    gap:1,
                                                    px:2,
                                                    borderRadius:4,
                                                    fontWeight:700,
                                                    fontSize:14,
                                                    fontFamily:"Roboto,sans-serif",
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        width:1,
                                                    },
                                                    "& .virtual_svg *" : {
                                                        fill: theme.palette.mode === "dark" ? colors.orange[400] : "#F56900"
                                                    }
                                                }}
                                            >
                                                {t("virtual_product")}
                                            </Box>
                                        </Box>
                                        {line.lines.map((prodLine, idx) => (
                                            <ButtonBase
                                                disabled={getReturnLineLoading}
                                                onClick={() => {
                                                    if(prodLine.state !== general_states.COMPLETED) {
                                                        handleOpen(prodLine.id)
                                                    }
                                                }}
                                                key={"inclines"+index}
                                                className={cx('', {
                                                    'active' : prodLine.id === returnLineToProcess?.id,
                                                    'completed': prodLine.state === general_states.COMPLETED,
                                                })}
                                                sx={{
                                                    mt:1,
                                                    width:1,
                                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                    border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                                    boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.25)" : null,
                                                    borderRadius:3,
                                                    px:2,
                                                    py:2,
                                                    gap:2,
                                                    display:"flex",
                                                    flexDirection:'column',
                                                    textAlign:"left",
                                                    "&.active": {
                                                        background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                                    },
                                                    "&.completed": {
                                                        background: colors.green[100],
                                                        border:`1px solid ${colors.green[400]}`
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width:1,
                                                        display:"flex",
                                                        alignItems:"flex-start",
                                                        justifyContent: "space-between",
                                                    }}
                                                >
                                                
                                                    <Box>
                                                        <Text bold>
                                                            {prodLine.product.description}
                                                        </Text><br/>
                                                        <Text>
                                                            {prodLine.product.barcodes[0]}
                                                        </Text>
                                                    </Box>

                                                    {prodLine.state !== general_states.COMPLETED &&
                                                        <Box
                                                            className={cx('', {
                                                                'active' : prodLine.id === returnLineToProcess?.id,
                                                            })}
                                                            sx={{
                                                                flexShrink:0,
                                                                px:2,
                                                                height:32,
                                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                                lineHeight:"32px",
                                                                fontSize:14,
                                                                fontFamily:"Roboto, sans-serif",
                                                                borderRadius:4,
                                                                color:colors.txt["primary"],
                                                                "&.active": {
                                                                    background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                                                    color:theme.palette.mode === "dark" ? colors.orange[400] : colors.orange[400],
                                                                }
                                                            }}
                                                        >
                                                            <b>{prodLine.amount}</b> 
                                                        </Box>
                                                    }
                                                </Box>
                                                {prodLine.returnreason?.description?.length > 0 &&
                                                    <Box
                                                        sx={{
                                                            width:1,
                                                            textAlign:"left",
                                                        }}
                                                    >
                                                        <Text light>{prodLine.returnreason?.description}</Text>
                                                    </Box>
                                                }
                                                {prodLine.state === general_states.COMPLETED &&
                                                    <Box
                                                        sx={{
                                                            // mt:2,
                                                            display:"flex",
                                                            gap:3,
                                                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                            width:1,
                                                            p:1,
                                                            px:1.5,
                                                            borderRadius:2,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                display:'flex',
                                                                flexDirection:'column'
                                                            }}
                                                        >
                                                            <Text variant={"green"}>{t('accepted_count')}</Text>
                                                            <Text>{prodLine.acceptedCount}</Text>
                                                        </Box>

                                                        <Box
                                                            sx={{
                                                                display:'flex',
                                                                flexDirection:'column'
                                                            }}
                                                        >
                                                            <Text variant={"red"}>{t('rejected_count')}</Text>
                                                            <Text>{prodLine.rejectedCount}</Text>
                                                        </Box>
                                                    </Box>
                                                }
                                            </ButtonBase>
                                        ))}
                                        
                                    </Box>
                                )
                            }
                            line = line.lines[0];
                            return(
                                <ButtonBase
                                    disabled={getReturnLineLoading}
                                    onClick={() => handleOpen(line.id)}
                                    key={"inclines"+index}
                                    className={cx('', {
                                        'active' : line.id === returnLineToProcess?.id,
                                    })}
                                    sx={{
                                        width:1,
                                        mt:1.5,
                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                        border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                        boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.25)" : null,
                                        borderRadius:3,
                                        px:2,
                                        py:2,
                                        gap:2,
                                        display:"flex",
                                        flexDirection:'column',
                                        textAlign:"left",
                                        "&.active": {
                                            background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                        }
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width:1,
                                            display:"flex",
                                            alignItems:"flex-start",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                    
                                        <Box>
                                            <Text bold>
                                                {line.product.description}
                                            </Text><br/>
                                            <Text>
                                                {line.product.barcodes[0]}
                                            </Text>
                                        </Box>

                                        <Box
                                            className={cx('', {
                                                'active' : line.id === returnLineToProcess?.id,
                                            })}
                                            sx={{
                                                flexShrink:0,
                                                px:2,
                                                height:32,
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                lineHeight:"32px",
                                                fontSize:14,
                                                fontFamily:"Roboto, sans-serif",
                                                borderRadius:4,
                                                color:colors.txt["primary"],
                                                "&.active": {
                                                    background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                                    color:theme.palette.mode === "dark" ? colors.orange[400] : colors.orange[400],
                                                }
                                            }}
                                        >
                                            <b>{line.amount}</b> 
                                        </Box>
                                    </Box>
                                    {line.returnreason?.description?.length > 0 &&
                                        <Box
                                            sx={{
                                                width:1,
                                                textAlign:"left",
                                            }}
                                        >
                                            <Text light>{line.returnreason?.description}</Text>
                                        </Box>
                                    }
                                </ButtonBase>
                            )
                        })}
                        
                        {completedReturnLines?.length > 0 &&
                            <>
                                <Heading color={colors.green[400]} sx={{mt:5,}}>{t("processed")}</Heading>

                                {completedReturnLines?.map((line,index) => {
                                    if(line.parentProduct) {
                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                                    p:1,
                                                    mt:1.5,
                                                    borderRadius:4,
                                                }}
                                            >

                                                <Box
                                                    sx={{
                                                        display:"flex",
                                                        gap:3,
                                                        justifyContent:'space-between',
                                                        p:1,
                                                        pb:0,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display:"flex",
                                                            flexDirection:'column',

                                                        }}
                                                    >

                                                        <Text bold>{line.parentProduct.description}</Text>
                                                        <Text>{line.parentProduct.sku}</Text>
                                                    </Box>
                                                   
                                                    <Box
                                                        sx={{
                                                            flexShrink:0,
                                                            px:2,
                                                            height:32,
                                                            background: colors.green[100],
                                                            lineHeight:"32px",
                                                            fontSize:14,
                                                            fontFamily:"Roboto, sans-serif",
                                                            borderRadius:4,
                                                            color: colors.green[400],
                                                            fontWeight:"bold"
                                                        }}
                                                    >
                                                        {t("completed")}
                                                    </Box>
                                                </Box>
                                                {line.lines.map((prodLine, idx) => (
                                                    <ButtonBase
                                                        disabled={getReturnLineLoading}
                                                        onClick={() => {
                                                            if(prodLine.state !== general_states.COMPLETED) {
                                                                handleOpen(prodLine.id)
                                                            }
                                                        }}
                                                        key={"inclines"+index}
                                                        className={cx('', {
                                                            'active' : prodLine.id === returnLineToProcess?.id,
                                                            'completed': prodLine.state === general_states.COMPLETED,
                                                        })}
                                                        sx={{
                                                            mt:1,
                                                            width:1,
                                                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                            border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                                            boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.25)" : null,
                                                            borderRadius:3,
                                                            px:2,
                                                            py:2,
                                                            gap:2,
                                                            display:"flex",
                                                            flexDirection:'column',
                                                            textAlign:"left",
                                                            "&.active": {
                                                                background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                                            },
                                                            "&.completed": {
                                                                background: colors.green[100],
                                                                border:`1px solid ${colors.green[400]}`
                                                            }
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width:1,
                                                                display:"flex",
                                                                alignItems:"flex-start",
                                                                justifyContent: "space-between",
                                                            }}
                                                        >
                                                        
                                                            <Box>
                                                                <Text bold>
                                                                    {prodLine.product.description}
                                                                </Text><br/>
                                                                <Text>
                                                                    {prodLine.product.barcodes[0]}
                                                                </Text>
                                                            </Box>

                                                            <Box
                                                                className={cx('', {
                                                                    'active' : prodLine.id === returnLineToProcess?.id,
                                                                })}
                                                                sx={{
                                                                    flexShrink:0,
                                                                    px:2,
                                                                    height:32,
                                                                    background: prodLine.state === general_states.DECLINED ? colors.red[100] : colors.green[100],
                                                                    lineHeight:"32px",
                                                                    fontSize:14,
                                                                    fontFamily:"Roboto, sans-serif",
                                                                    borderRadius:4,
                                                                    color: prodLine.state === general_states.DECLINED ? colors.red[400] : colors.green[400],
                                                                    fontWeight:"bold"
                                                                }}
                                                            >
                                                                {t(prodLine.state)}
                                                            </Box>
                                                        </Box>
                                                        {prodLine.returnreason?.description?.length > 0 &&
                                                            <Box
                                                                sx={{
                                                                    width:1,
                                                                    textAlign:"left",
                                                                }}
                                                            >
                                                                <Text light>{prodLine.returnreason?.description}</Text>
                                                            </Box>
                                                        }
                                                        {prodLine.state === general_states.COMPLETED &&
                                                            <Box
                                                                sx={{
                                                                    // mt:2,
                                                                    display:"flex",
                                                                    gap:3,
                                                                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                                    width:1,
                                                                    p:1,
                                                                    px:1.5,
                                                                    borderRadius:2,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:'flex',
                                                                        flexDirection:'column'
                                                                    }}
                                                                >
                                                                    <Text variant={"green"}>{t('accepted_count')}</Text>
                                                                    <Text>{prodLine.acceptedCount}</Text>
                                                                </Box>

                                                                <Box
                                                                    sx={{
                                                                        display:'flex',
                                                                        flexDirection:'column'
                                                                    }}
                                                                >
                                                                    <Text variant={"red"}>{t('rejected_count')}</Text>
                                                                    <Text>{prodLine.rejectedCount}</Text>
                                                                </Box>
                                                            </Box>
                                                        }
                                                    </ButtonBase>
                                                ))}
                                                
                                            </Box>
                                        )
                                    }
                                    line = line.lines[0];
                                    return (
                                        <Box
                                            key={"inclines"+index}
                                            className={cx('', {
                                                'active' : line.id === returnLineToProcess?.id,
                                            })}
                                            sx={{
                                                mt:1.5,
                                                flex:1,
                                                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                                                border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                                boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.25)" : null,
                                                borderRadius:3,
                                                px:2,
                                                py:2,
                                                gap:2,
                                                "&.active": {
                                                    background:theme.palette.mode === "dark" ? colors.orange[100] : colors.orange[100],
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{

                                                    display:"flex",
                                                    alignItems:"flex-start",
                                                    justifyContent: "space-between",
                                                }}
                                            >
                                            
                                                <Box>
                                                    <Text bold>
                                                        {line.product?.description}
                                                    </Text><br/>
                                                    <Text light>
                                                        {line?.internalReason}
                                                    </Text>
                                                </Box>
                                                <Box
                                                    className={cx('', {
                                                        'active' : line.id === returnLineToProcess?.id,
                                                    })}
                                                    sx={{
                                                        flexShrink:0,
                                                        px:2,
                                                        height:32,
                                                        background: line.state === general_states.DECLINED ? colors.red[100] : colors.green[100],
                                                        lineHeight:"32px",
                                                        fontSize:14,
                                                        fontFamily:"Roboto, sans-serif",
                                                        borderRadius:4,
                                                        color: line.state === general_states.DECLINED ? colors.red[400] : colors.green[400],
                                                        fontWeight:"bold"
                                                    }}
                                                >
                                                    {t(line.state)}
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    mt:2,
                                                    display:"flex",
                                                    gap:3,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:'flex',
                                                        flexDirection:'column'
                                                    }}
                                                >
                                                    <Text variant={"green"}>{t('accepted_count')}</Text>
                                                    <Text>{line.acceptedCount}</Text>
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display:'flex',
                                                        flexDirection:'column'
                                                    }}
                                                >
                                                    <Text variant={"red"}>{t('rejected_count')}</Text>
                                                    <Text>{line.rejectedCount}</Text>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )
                                })}
                            </>
                        } 
                       
                    </Box>

                    <Box
                        width={120}
                        sx={{
                            display: "flex",
                            paddingBottom: 3,
                            justifyContent: "center",
                            alignItems: "center",
                            color: colors.grey[300],
                            position: "relative", // For controlling the layout and positioning
                            "@media screen and (max-width: 1080px)": {
                                transform: "rotate(90deg)",
                                width: 380,
                            },
                        }}
                    >
                        {/* Wrapper Box for animation */}
                        <Box
                            sx={{
                                overflow:"hidden",
                                width: "100%",
                                // height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <img
                                className="supra-arrow"  // Keep the class for potential future use
                                src="/img/arrow.svg"
                                alt="Barcode"
                                style={{
                                    width: "40%",   // Image size
                                    height: "40%",  // Image size
                                    objectFit: "contain", // Ensures the image fits the container
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                           

              
            </Box>
            
            <Box
                sx={{
                    background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],

                    flex:1,
                    p:2,
                    borderRadius:6,
                    overflow:'hidden',
                    // borderTopLeftRadius:24,
                    // borderTopRightRadius:24,
                    flexDirection: "column",
                    display:"flex",
                }}
            >
                 {returnLineToProcess !== null ?
                     <Box
                        sx={{
                            flex:1,
                            background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                            p:2,
                            borderRadius:4,
                            overflow:"auto",
                            // pb: getPaddingBottom()
                        }}
                    >

                        <Box
                            sx={{
                                display:"flex",
                                gap:3,
                                alignItems:"flex-start",
                            }}
                        >
                            <Box
                                sx={{
                                    flex:1,
                                    overflow:'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        display:"flex",
                                        overflow:'hidden',
                                    cursor:"pointer"
                                    }}
                                    onClick={() => {
                                        if(!spProductLoading) {
                                            setSpProductLoading(true);
                                        } 
                                        setProductsSpIsExtended(true);
                                    }}
                                >
                                    <Heading
                                        sx={{
                                            mb:2,
                                        }}
                                    >
                                        {returnLineToProcess.product?.description}
                                    </Heading>
                                    <OpenInNewIcon
                                        sx={{
                                            ml:1,
                                        }}    
                                    />
                                </Box>

                                {returnLineToProcess.product?.returnInstruction?.trim() && (
                                    <Box
                                        sx={{
                                            mb:1.5,
                                            flex: 1,
                                            background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                                            p: 1.5,
                                            display: "flex",
                                            flexDirection: "column",
                                            borderRadius: 4,
                                        }}
                                    >
                                        <Text>{t("return_instruction")}</Text>
                                        <Text bold>{returnLineToProcess.product?.returnInstruction}</Text>
                                    </Box>
                                )}

                                {!props.incData.rejectAll && !returnLineToProcess.product.rejectOnReturn ? 
                                    <>
                                        <Box
                                            sx={{
                                                px:3,
                                                py:2,
                                                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                borderRadius:5,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display:'flex',
                                                    alignItems:"center"
                                                }}
                                            >
                                                <Text
                                                    sx={{
                                                        width:120,
                                                        flexShrink:0,
                                                    }}
                                                >
                                                    {t("reason")}
                                                </Text>

                                                <Box
                                                    sx={{
                                                        flex:1,
                                                        minHeight:32,
                                                        lineHeight:"32px",
                                                        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                                        px:2,
                                                        borderRadius:4,

                                                    }}
                                                >
                                                    <Text semibold>{returnLineToProcess.returnreason?.description ? `${returnLineToProcess.returnreason?.description}${returnLineToProcess.remark && returnLineToProcess.remark.length > 0 ? ` - ${returnLineToProcess.remark}` : ""}` : (returnLineToProcess.remark && returnLineToProcess.remark.trim() !== "" ? returnLineToProcess.remark : returnLineToProcess.externalReturnReason)}</Text>
                                                </Box>
                                            </Box>  
                                            <Box
                                                sx={{
                                                    mt:1.5,
                                                    display:'flex',
                                                    alignItems:"flex-start"
                                                }}
                                            >
                                                <Text
                                                    sx={{
                                                        width:120,
                                                        flexShrink:0,
                                                        lineHeight:"32px",
                                                    }}
                                                >
                                                    {t("internal_reason")}
                                                </Text>
                                                
                                                <Box
                                                    sx={{
                                                        flex:1,
                                                    }}
                                                >
                                                    <ButtonBase
                                                        sx={{
                                                            width:1,
                                                            pl:"16px",
                                                            pr:"16px",
                                                            borderRadius:4,
                                                            background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                                            border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                                            boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,

                                                        }}
                                                    >
                                                        <DropdownBase
                                                            key={returnLineToProcess.internalReasonIndex}
                                                            displayName={"description"} 
                                                            onChange={(selected) => {
                                                                setReturnLineToProcess(line => ({
                                                                    ...line,
                                                                    internalReason: (selected + 1) !== (extendedReturnReasons.length) ? extendedReturnReasons[returnLineToProcess.internalReasonIndex]?.description : "",
                                                                    internalReasonIndex:selected
                                                                }));
                                                            }}
                                                            noSelection={""} //Custom text when nothing is selected
                                                            options={extendedReturnReasons ?? []} // array
                                                            selected={extendedReturnReasons[returnLineToProcess.internalReasonIndex] ?? -1}
                                                            closeOnSelection
                                                            maxHeight={"unset"}
                                                            disallowDeselection
                                                        >
                                                            <Box 
                                                                sx={{
                                                                    width:1,
                                                                    display:"inline-flex",
                                                                    alignItems:"center",
                                                                    gap:1,
                                                                    height:32,
                                                                
                                                                    // "@media screen and (max-width: 47.9375em)" : {
                                                                    //     display:"inline",
                                                                    //     width:"unset",
                                                                    //     // width:1,
                                                                    //     // display:"flex",
                                                                    //     // justifyContent:"flex-end",
                                                                    //     ml:0,
                                                                    //     mr:"-8px"
                                                                    // }
                                                                }}
                                                            >
                                                                <Text bold>{returnLineToProcess.internalReasonIndex !== -1 ? extendedReturnReasons[returnLineToProcess.internalReasonIndex]?.description : t("no_selection")}</Text>
                                                            </Box>
                                                        </DropdownBase>
                                                    </ButtonBase>

                                                    {extendedReturnReasons.length > 0 && (returnLineToProcess.internalReasonIndex + 1) === extendedReturnReasons.length &&
                                                        <Box>
                                                            <InputBase
                                                                multiline
                                                                rows={4}
                                                                value={returnLineToProcess.internalReason}
                                                                onChange={(e) => {
                                                                    setReturnLineToProcess(line => ({
                                                                        ...line,
                                                                        internalReason: e.target.value,
                                                                    }))
                                                                }}
                                                                placeholder={t("internal_reason")}
                                                                sx={{
                                                                    mt:1,
                                                                    width: '100%',
                                                                    fontSize: 14,
                                                                    px:2,
                                                                    py:1.5,
                                                                    background:theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200],
                                                                    borderRadius: 5,
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    <Box>
                                                        <InputBase
                                                            multiline
                                                            rows={4}
                                                            value={returnLineToProcess.internalRemark}
                                                            onChange={(e) => {
                                                                setReturnLineToProcess(line => ({
                                                                    ...line,
                                                                    internalRemark: e.target.value,
                                                                }))
                                                            }}
                                                            placeholder={t("remark")}
                                                            sx={{
                                                                mt:1,
                                                                width: '100%',
                                                                fontSize: 14,
                                                                px:2,
                                                                py:1.5,
                                                                background:theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200],
                                                                borderRadius: 5,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Subheading sx={{
                                            mt:3,
                                            mb:1,
                                            display:"flex",
                                            alignItems:'center',
                                        }}>
                                            {t("to_assess")}
                                            <EMReturnProcessing />
                                            <Box
                                                sx={{
                                                    ml:1.5,
                                                    textAlign:'center',
                                                    lineHeight:"24px",
                                                    width:24,
                                                    height:24,
                                                    borderRadius:4,
                                                    background: theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[100],
                                                }}
                                            >
                                                {returnLineToProcess.amount}
                                            </Box>

                                            <Box
                                                sx={{
                                                    marginLeft:"auto",
                                                    display:"flex",
                                                    alignItems:"center",
                                                    gap:1,
                                                }}
                                            >
                                                <Text noBreak>{t("not_everything_received")}</Text>
                                                <ShSwitch
                                                    checked={!everythingAccountedFor && returnLineToProcess.notEverythingReceived === true}
                                                    disabled={everythingAccountedFor}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setReturnLineToProcess(line => ({
                                                            ...line,
                                                            notEverythingReceived: checked,
                                                        }));
                                                    }}
                                                    color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                />
                                            </Box>

                                        </Subheading>

                                        <Box
                                            sx={{
                                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                                border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                                boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
                                                p:3,
                                                borderRadius:5,
                                                display:"flex",
                                                flexDirection:'column',
                                                gap:2,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    
                                                    gap:2,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:"flex",
                                                        flexDirection:'column',
                                                        flex:1,
                                                    }}
                                                >
                                                    <Text bold variant={"green"}>{t("accepted")}</Text>
                                                    <ShValidatedNumberInput
                                                    min={0}
                                                        max={returnLineToProcess.notEverythingReceived ? Math.max(0, returnLineToProcess.amount - (returnLineToProcess.rejectedCount ?? 0)) : null}
                                                        value={returnLineToProcess.acceptedCount}
                                                        onlyPositive
                                                        onChange={(name,amount) => {
                                                            setReturnLineToProcess(line => (
                                                                line.notEverythingReceived
                                                                ? { ...line, acceptedCount: Math.max(0, Math.min(amount, line.amount - (line.rejectedCount ?? 0))) }
                                                                // Fill the remainder only when the count goes up; lowering accepted
                                                                // must not silently bump rejected back up.
                                                                : {
                                                                    ...line,
                                                                    acceptedCount: amount,
                                                                    ...(amount > (line.acceptedCount ?? 0) ? { rejectedCount: Math.max(0, line.amount - amount) } : {})
                                                                }
                                                            ))
                                                        }}
                                                        sx={{
                                                            mt:1,
                                                        }}
                                                        inputSX={{
                                                            height:32,
                                                            borderRadius:4,
                                                            background:colors.green[100]
                                                        }}
                                                    />
                                                </Box>

                                                <Box
                                                    sx={{
                                                        display:"flex",
                                                        flexDirection:'column',
                                                        flex:1,
                                                    }}
                                                >
                                                    <Text bold variant={"red"}>{t("rejected")}</Text>
                                                    <ShValidatedNumberInput
                                                    min={0}
                                                        max={returnLineToProcess.notEverythingReceived ? Math.max(0, returnLineToProcess.amount - (returnLineToProcess.acceptedCount ?? 0)) : null}
                                                        value={returnLineToProcess.rejectedCount}
                                                        onlyPositive
                                                        onChange={(name,amount) => {
                                                            setReturnLineToProcess(line => (
                                                                line.notEverythingReceived
                                                                ? { ...line, rejectedCount: Math.max(0, Math.min(amount, line.amount - (line.acceptedCount ?? 0))) }
                                                                // Fill the remainder only when the count goes up; lowering rejected
                                                                // must not silently bump accepted back up.
                                                                : {
                                                                    ...line,
                                                                    rejectedCount: amount,
                                                                    ...(amount > (line.rejectedCount ?? 0) ? { acceptedCount: Math.max(0, line.amount - amount) } : {})
                                                                }
                                                            ))
                                                        }}
                                                        sx={{
                                                            mt:1,
                                                        }}
                                                        inputSX={{
                                                            height:32,
                                                            borderRadius:4,
                                                            background:colors.red[100]
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                            {returnLineToProcess.product?.serialNumberRequired &&
                                                (() => {
                                                    const scannedCount = returnLineToProcess.serialnumbers?.length ?? 0;
                                                    const mismatch = scannedCount !== returnLineToProcess.acceptedCount;
                                                    return (
                                                        <Box
                                                            sx={{
                                                                mt:1,
                                                                display:"flex",
                                                                flexDirection:"column",
                                                                gap:1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:"flex",
                                                                    justifyContent:"space-between",
                                                                    alignItems:"flex-end",
                                                                }}
                                                            >
                                                                <Subheading>{t("serial_numbers")}</Subheading>
                                                                <Text light>{scannedCount}/{returnLineToProcess.acceptedCount}</Text>
                                                            </Box>
                                                            <Box
                                                                sx={{
                                                                    p:2,
                                                                    background: mismatch
                                                                        ? colors.red[100]
                                                                        : (theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[200]),
                                                                    border: mismatch ? `1px solid ${colors.red[400]}` : null,
                                                                    borderRadius:10,
                                                                }}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display:'flex',
                                                                        height:32,
                                                                        background: theme.palette.mode === "dark" ? colors.grey[800] : colors.txt["white"],
                                                                        justifyContent:"space-between",
                                                                        borderRadius:5,
                                                                        "@media screen and (max-width: 47.9375em)": {
                                                                            flexDirection:"column"
                                                                        }
                                                                    }}
                                                                >
                                                                    <img
                                                                        src="/img/barcode.svg"
                                                                        alt="Barcode"
                                                                        style={{
                                                                            width: '24px',
                                                                            marginLeft: "16px",
                                                                        }}
                                                                    />
                                                                    <InputBase
                                                                        inputRef={serialNumberRef}
                                                                        sx={{
                                                                            ml: 2,
                                                                            flex:1,
                                                                            width: 248,
                                                                            textAlign: 'center',
                                                                            justifyContent: 'center'
                                                                        }}
                                                                        placeholder={t("scan_type_barcode")}
                                                                        onKeyDown={handleSerialnumber}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            {(returnLineToProcess.serialnumbers ?? []).map((serialNumber, idex) => (
                                                                <Box
                                                                    key={"sn"+idex}
                                                                    sx={{
                                                                        height:32,
                                                                        background: theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[100],
                                                                        borderRadius:5,
                                                                        pl:2,
                                                                        pr:0.5,
                                                                        lineHeight:"32px",
                                                                        display:'flex',
                                                                        alignItems:'center',
                                                                        justifyContent:'space-between',
                                                                    }}
                                                                >
                                                                    <Text>{serialNumber.barcode}</Text>
                                                                    <ButtonBase
                                                                        onClick={() => {
                                                                            removeSerialnumber(serialNumber.barcode);
                                                                            if(serialNumberRef.current){
                                                                                serialNumberRef.current.focus();
                                                                            }
                                                                        }}
                                                                        sx={{
                                                                            height:24,
                                                                            width:24,
                                                                            borderRadius:4,
                                                                        }}
                                                                    >
                                                                        <CloseIcon fontSize="small"/>
                                                                    </ButtonBase>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    );
                                                })()
                                            }
                                        </Box>
                                    </> 
                                : 
                                    <>
                                        {props.incData.rejectAll &&
                                            <Box
                                                sx={{
                                                    width:1,
                                                    background:colors.red[100],
                                                    p:3,
                                                    borderRadius:4,
                                                    minHeight:156,
                                                    display:"flex",
                                                    alignItems:'center',
                                                    justifyContent:'center',
                                                }}
                                            >
                                                <Text center variant="red" semibold>
                                                    {t("rejectAll")}
                                                </Text>
                                            </Box>
                                        }
                                        {returnLineToProcess.product.rejectOnReturn &&
                                            <Box
                                                sx={{
                                                    width:1,
                                                    background:colors.red[100],
                                                    p:3,
                                                    borderRadius:4,
                                                    minHeight:156,
                                                    display:"flex",
                                                    alignItems:'center',
                                                    justifyContent:'center',
                                                }}
                                            >
                                                <Text center variant="red" semibold>
                                                    {t("rejectOnReturn")}
                                                </Text>
                                            </Box>
                                        }
                                        <Box>
                                            <InputBase
                                                multiline
                                                rows={4}
                                                value={returnLineToProcess.internalRemark}
                                                onChange={(e) => {
                                                    setReturnLineToProcess(line => ({
                                                        ...line,
                                                        internalRemark: e.target.value,
                                                    }))
                                                }}
                                                placeholder={t("remark")}
                                                sx={{
                                                    mt:1,
                                                    width: '100%',
                                                    fontSize: 14,
                                                    px:2,
                                                    py:1.5,
                                                    background:theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[100],
                                                    borderRadius: 5,
                                                }}
                                            />
                                        </Box>
                                    </>
                                }

                                <LinePhotos
                                    photos={photosData || []}
                                    onAddFromPC={() => handleOpenPhotoModal('pc')}
                                    onAddFromScanner={() => handleOpenPhotoModal('scanner')}
                                    onDelete={handleDeletePhoto}
                                    onPhotoClick={handlePhotoClick}
                                    isLoading={photosLoading || isDeletingPhoto}
                                />

                            </Box>

                            <Box
                                sx={{
                                    flex:1,
                                }}
                            >

                                <Box
                                    sx={{
                                        mb:4,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display:'flex',
                                            alignItems:'center',
                                        }}
                                    >
                                        <Heading>
                                            {props.incData.reference}
                                        </Heading>
                                        <Subheading color={colors.txt["secondary"]}>
                                            &nbsp;&nbsp;/&nbsp;&nbsp;{returnLineToProcess?.product?.relation?.name}
                                        </Subheading>
                                    </Box>
                                    <Box
                                        sx={{
                                            mt:2,
                                            width:1,
                                            borderRadius:4,
                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[50],
                                            border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                            boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.25)" : null,
                                            p:2,
                                        }}
                                    >
                                        <Text semibold >{props.incData.name}</Text>

                                        <Box
                                            sx={{
                                                mt:2,
                                                display:'flex',
                                                gap:3,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Text light>{t("phone")}</Text>
                                                <Text>{props.incData.phone}</Text>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display:"flex",
                                                    flexDirection:'column',
                                                }}
                                            >
                                                <Text light>{t("email")}</Text>
                                                <Text>{props.incData.email}</Text>
                                            </Box>

                                        </Box>
                                        
                                        {props.incData.trackandtrace !== null &&
                                            <Box
                                                sx={{
                                                    mt:2,
                                                    width:1,
                                                    height:24,
                                                    borderRadius:4,
                                                    background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                    textAlign:'center',
                                                    lineHeight:'24px'
                                                }}
                                            >
                                                <Text semibold variant={theme.palette.mode === "dark" ? "blue" : "primary"}>{props.incData.trackandtrace}</Text>
                                            </Box>
                                        }
                                    </Box>
                                    {(!props.incData.rejectAll && !returnLineToProcess.product.rejectOnReturn) && (
                                        <>
                                            {returnLineToProcess?.hasReturnLocation ?
                                                <Box
                                                    sx={{
                                                        mt:2,
                                                        p:1.5,
                                                        overflow:"hidden",
                                                        borderRadius:3,
                                                        background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50]
                                                    }}
                                                >
                                                    <Text bold>{t("locations")}</Text>
                                                    <Box
                                                        sx={{
                                                            mt:1,
                                                            mb:1,
                                                            display:"flex",
                                                            flexDirection:"column",
                                                            gap:1,
                                                        }}
                                                    >

                                                        {returnLineToProcess?.product?.stocks?.map((stock,idex) => {
                                                            if(stock.warehouseLocation.type !== warehouselocationtype_type.GENERAL) return;

                                                            const labelData = getWarehouseLocationLabelData(stock.warehouseLocation.type,theme)
                                                            return (
                                                                <Box
                                                                    key={"location"+idex}
                                                                    sx={{
                                                                        width:1,
                                                                        background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                                                        display:"flex",
                                                                        height:32,
                                                                        alignItems:'center',
                                                                        pl:1.5,
                                                                        pr:0.5,
                                                                        borderRadius:4,
                                                                        justifyContent:'space-between',
                                                                        border:`1px solid ${colors.grey[100]}`,
                                                                        boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                                                    }}
                                                                >
                                                                    <Text>
                                                                        {stock.warehouseLocation.location}
                                                                    </Text>

                                                                    <ShLabel
                                                                        borderless
                                                                        size={24}
                                                                        fitted
                                                                        variant={theme.palette.mode === "dark" ? "greyLight" : "grey"}
                                                                        palette="normal"
                                                                    >
                                                                        {stock.available + " "}
                                                                        <Text light>{t("available")}</Text>
                                                                    </ShLabel>

                                                                    <ShLabel
                                                                        borderless
                                                                        size={24}
                                                                        fitted
                                                                        variant={labelData.labelcolor}
                                                                        palette="normal"
                                                                    >
                                                                        {t(labelData.labeltext)}
                                                                    </ShLabel>
                                                                </Box>
                                                            )
                                                        })}
                                                    </Box>
                                                </Box>
                                            :
                                                <Box
                                                    sx={{
                                                        mt:2,
                                                        p:1.5,
                                                        overflow:"hidden",
                                                        borderRadius:3,
                                                        background:theme.palette.mode === "dark" ? colors.blue[200] : colors.blue[200]
                                                    }}
                                                >
                                                    <Text bold>{t("choose_a_location_to_move_to")}</Text>
                                                    <Box
                                                        sx={{
                                                            mt:1,
                                                            mb:1,
                                                            display:"flex",
                                                            flexDirection:"column",
                                                        }}
                                                    >
                                                        {!suggestedLocationsLoading &&
                                                            suggestedLocations.availableLocations.map((warehouse,idex) => {
                                                                return (
                                                                    <Box
                                                                        key={"warehouse"+idex}
                                                                        sx={{
                                                                            mb:3,
                                                                            display:"flex",
                                                                            flexDirection:'column',
                                                                            gap:1,
                                                                        }}
                                                                    >
                                                                        {
                                                                            warehouse.locations.map((location,locIdex) => {
                                                                                if(location.locationType !== warehouselocationtype_type.GENERAL) return;

                                                                                const labelData = getWarehouseLocationLabelData(location.locationType,theme)
                                                                                return (
                                                                                    <ButtonBase
                                                                                        key={"locidx"+locIdex}
                                                                                        onClick={() => {
                                                                                            setReturnLineToProcess(line => ({
                                                                                                ...line,
                                                                                                selectedWarehouseLocationIndex:-1,
                                                                                                warehouseLocationId:location.id,
                                                                                                chosenLocation:{
                                                                                                    id:location.id,
                                                                                                    name:location.location,
                                                                                                },
                                                                                            }));
                                                                                        }}
                                                                                        className={cx(undefined, {
                                                                                            "selected": returnLineToProcess?.warehouseLocationId === location.id,
                                                                                        })}
                                                                                        sx={{
                                                                                            width:1,
                                                                                            background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                                                                            display:"flex",
                                                                                            height:32,
                                                                                            alignItems:'center',
                                                                                            pl:1.5,
                                                                                            pr:0.5,
                                                                                            borderRadius:4,
                                                                                            justifyContent:'space-between',
                                                                                            border:`1px solid ${colors.grey[100]}`,
                                                                                            boxShadow:`0px 2px 4px ${colors.grey[200]}`,
                                                                                            "&.selected" : {
                                                                                                background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                                                                                border:`1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <Text>
                                                                                            {location.location}
                                                                                        </Text>

                                                                                        <ShLabel
                                                                                            borderless
                                                                                            size={24}
                                                                                            fitted
                                                                                            variant={theme.palette.mode === "dark" ? "greyLight" : "grey"}
                                                                                            palette="normal"
                                                                                        >
                                                                                            {location.stock + " "}
                                                                                            <Text light>{t("available")}</Text>
                                                                                        </ShLabel>

                                                                                        <ShLabel
                                                                                            borderless
                                                                                            size={24}
                                                                                            fitted
                                                                                            variant={labelData.labelcolor}
                                                                                            palette="normal"
                                                                                        >
                                                                                            {t(labelData.labeltext)}
                                                                                        </ShLabel>
                                                                                    </ButtonBase>
                                                                                );
                                                                            })
                                                                        }
                                                                    </Box>
                                                                )
                                                            })
                                                            
                                                        }
                                                        <ShDropdownSearchQuery
                                                            name="name"
                                                            onChange={(selected,option) => {
                                                                if(selected === -1) return;

                                                                setReturnLineToProcess(line => ({
                                                                    ...line,
                                                                    selectedWarehouseLocationIndex:selected,
                                                                    warehouseLocationId:option.id,
                                                                    chosenLocation:option,
                                                                }));
                                                            }}
                                                            displayName={"name"}
                                                            content={useGetWarehouseLocationSelectQuery}
                                                            closeOnSelection
                                                            multiple={false}
                                                            selected={returnLineToProcess?.selectedWarehouseLocationIndex ?? -1}
                                                            // selected={-1}
                                                            searchOptions={{
                                                                placeholder:t("search")
                                                            }}
                                                        >
                                                            {({anchorEl,onClick}) => (
                                                                <ButtonBase
                                                                    onClick={onClick}
                                                                    sx={{
                                                                        width:1,
                                                                        height:32,
                                                                        borderRadius:5,
                                                                        background:theme.palette.mode === "dark" ? colors.blue[400] : colors.blue[400],
                                                                        display:"flex",
                                                                        alignItems:'center',
                                                                        px:0.5,
                                                                        position:'relative',
                                                                        
                                                                    }}
                                                                >
                                                                    <Text bold color={theme.palette.mode === "dark" && colors.txt["primaryDark"]}>
                                                                        {returnLineToProcess?.chosenLocation ? returnLineToProcess?.chosenLocation.name : t("nothing_selected")}
                                                                    </Text>
                                                                </ButtonBase>
                                                            )}
                                                        </ShDropdownSearchQuery>
                                                    </Box>

                                                </Box>
                                            } 
                                        </>
                                    )}
                                    {returnLineToProcess.hasDuplicate && (
                                        <ReturnLocationDuplicate
                                            returnLineToProcess={returnLineToProcess}
                                        />
                                    )}

                                </Box> 
                            </Box>
                        </Box>
                        
                        {spProductLoading &&
                            <SPStockDetails
                                isExtended={productsSpIsExtended}
                                setIsExtended={setProductsSpIsExtended}
                                productId={returnLineToProcess.product.id}
                            />
                        }

                        <MLinePhotoCapture
                            open={photoModalOpen}
                            onClose={() => setPhotoModalOpen(false)}
                            barcodeValue={`${stockhubReturnPreCode}-${props.incData.id}-${returnLineToProcess?.id}`}
                            photos={photosData || []}
                            refetchPhotos={refetchPhotos}
                            onUpload={handleUploadPhoto}
                            onDelete={handleDeletePhotoById}
                            isUploading={isUploadingPhoto}
                            isDeleting={isDeletingPhoto}
                            isFetchingPhotos={photosFetching}
                            initialMode={photoModalMode}
                            onPhotosChanged={refetchPhotos}
                            onPhotoClick={handlePhotoClick}
                        />

                        <MPhotoViewer
                            open={photoViewerOpen}
                            onClose={() => setPhotoViewerOpen(false)}
                            photos={photosData || []}
                            currentPhotoIndex={currentPhotoIndex}
                            onPhotoChange={setCurrentPhotoIndex}
                            onDelete={photosData?.[currentPhotoIndex]?.source === "consumer" ? null : handleDeletePhoto}
                            isDeleting={isDeletingPhoto}
                        />

                        <MReturnLocationDuplicate
                            open={duplicateModalOpen}
                            handleClose={() => setDuplicateModalOpen(false)}
                            returnLineToProcess={returnLineToProcess}
                            onConfirm={doSave}
                        />

                    </Box>
                 
                 : 
                    <Box
                        sx={{
                            flex:1,
                            flexDirection: "column",
                            display:"flex",
                            p:2,
                            borderRadius:4,
                            position:'relative',
                            // borderTopLeftRadius:16,
                            // borderTopRightRadius:16,
                            background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],

                        }}
                    >

                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 4,
                                // marginTop: 3,
                                paddingTop:4,
                                overflow: "hidden",
                                display: "flex",
                                flexDirection: "column",
                                paddingBottom: 3,
                                justifyContent: 'center',  // To center contents vertically
                                alignItems: 'center',      // To center contents horizontally
                            }}
                        >
                            <img 
                                src="/img/barcode.svg" 
                                alt="Barcode" 
                                style={{
                                    width: '30%', 
                                    height: '30%', 
                                    objectFit: 'contain',
                                    animation: 'pulsate 1.5s infinite'
                                }} 
                            />

                            <Box display="flex"
                                height="32px"
                                // maxWidth="400px"
                                borderRadius="20px"
                                backgroundColor={colors.grey[100]}
                                sx={{
                                    marginTop:0.5,
                                    marginBottom:0.5,
                                    justifyContent:"space-between",
                                    "@media screen and (max-width: 47.9375em)" : {
                                        flexDirection:"column"
                                    }
                                }}
                            >
                                <InputBase 
                                    sx={{
                                        ml: 2, 
                                        flex:1, 
                                        width: 248, 
                                        textAlign: 'center', 
                                        justifyContent: 'center'
                                    }} 
                                    placeholder={t("scan_type_barcode")}
                                    onKeyDown={handleKeyDown}
                                    autoFocus={true}
                                />
                            </Box>
                        </Box>

                    </Box>
                } 

                {returnLineToProcess !== null &&
                    <Box
                        sx={{
                            mt:3,
                            display:"flex",
                            justifyContent:"flex-end",
                            alignItems:"center",
                        }}
                    >
                        <ButtonGroup version={2}>
                            {(!props.incData.rejectAll && !returnLineToProcess.product.rejectOnReturn)  ? 
                                <Shbutton 
                                    className={"group-btn"} 
                                    // isLoading={finalizeLoading || finalizeFetching} 
                                    color={theme.palette.mode === "dark" ? "blue" : "primary"} 
                                    variant="contained" 
                                    onClick={handleSave}
                                    isLoading={updatingReturnLine}
                                >
                                    {t("complete")}
                                </Shbutton>
                            : 
                                <Shbutton 
                                    className={"group-btn"} 
                                    color={"red"} 
                                    variant="contained" 
                                    onClick={handleSave}
                                    isLoading={updatingReturnLine}
                                >
                                    {t("reject")}
                                </Shbutton>
                            }
                        </ButtonGroup>
                    </Box>
                }
               
            </Box>

            {/* <MUpdateLot 
                // onOpen={() => updateStockModal.setOpen(true)} 
                lot={lotToUpdate} 
                onClose={() => updateStockModal.setOpen(false)} 
                open={updateStockModal.open} 
                handleClose={handleUpdateStockClose}
            
            />
            <MUpdateBatch 
                // onOpen={() => updateStockModal.setOpen(true)} 
                batch={batchToUpdate} 
                onClose={() => updateBatchModal.setOpen(false)} 
                open={updateBatchModal.open} 
                handleClose={handleUpdateBatchClose}
            
            />
             <MConfirmDelete
                open={confirmDeleteLotLotModal.open}
                handleClose={() => {
                    setLotToDelete(null);
                    confirmDeleteLotLotModal.setOpen(false)
                }}
                data={lotToDelete?.barcode}
                confirm={confirmDeleteLotLotModal.confirm}
            />
            <MConfirmDelete
               open={confirmDeleteBatchModal.open}
               handleClose={() => {
                   setBatchToDelete(null);
                   confirmDeleteBatchModal.setOpen(false)
               }}
               data={batchToDelete?.number}
               confirm={confirmDeleteBatchModal.confirm}
           /> */}
        </Box>
    )
}

export default ReturnsToProcessView;

const SelectWorkSpace = (props) => {
    const { t, ready } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const dispatch = useDispatch();
    const [data, setData] = useState([]);
    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);
    const [selected, setSelected] = useState(-1);
    const [workTSpace,setTWorkSpace] = useState(selectedWorkSpace);

    const select = (selected) => {
        setTWorkSpace(data[selected]);
    }
    const { data: workSpaceData, isLoading } = useFindWorkspacesSelectQuery();

    useEffect(() => {
        if (!isLoading && workSpaceData) {
            const modifiedArray = workSpaceData.data.map(item => ({
                ...item,
                title: item.name
              }));
            
            setData(modifiedArray);
        }
    }, [workSpaceData, isLoading]);


    const selectWorkSpace = () => {
        dispatch(setWorkSpace({
            id:workTSpace.id,
            name:workTSpace.name,
        }));
    }

    useEffect(() => {
        if(data.length > 0) {
            for(var i = 0;i < data.length; i++) { 
                if(data[i].id === selectedWorkSpace.id){
                    setSelected(i);
                    // setSelected
                }
            }
        }
    }, [selectedWorkSpace,data]);


    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    return(
        <Box
            sx={{
                // py:3,
                // px:2,
                borderRadius:4,
            }}
        >
            
            <Box 
                sx={{
                    // paddingY:4,
                    // paddingX:5,
                    display:"flex",
                    flexDirection:"column",
                    gap:1
                }}>
                <Subheading>{props.subheading || t("select_workspace")}</Subheading>
            </Box>
            <Box
                sx={{
                    pt:3,
                }}
            >
                <SelectGroup 
                    customColors={{
                        selected: {
                            background:themeSettings(theme.palette.mode).palette.green.main,
                            foreground: themeSettings(theme.palette.mode).palette.green.contrastText,
                        },
                        default: {
                            background:colors.green[100],
                            foreground: colors.green[400],
                        }
                    }}
                
                selected={selected} setSelected={select} options={data} />
            </Box>
            
            <Box
                sx={{
                    paddingTop:3,
                    "@media screen and (max-width: 47.9375em)" : {
                        paddingRight:0,
                    }
                }}
            >
                <Shbutton block className={"group-btn"} color={theme.palette.mode === "dark" ? "primaryGreyOpposite" : "grey"} onClick={() => selectWorkSpace()} variant="contained">{t('select')}</Shbutton>
            </Box>
        </Box>
    )
}

const ReturnLocationDuplicate = ({returnLineToProcess}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 

    return (
        <Box
            sx={{
                borderRadius:4,
                mt:2,
                background:colors.orange[100],
                p:2,
                py:1.5,
                display:"flex",
                flexDirection:'column',
            }}
        >
            <Subheading variant="orange"
                sx={{
                    overflow:"visible",
                    whiteSpace:"normal"
                }}
            >
                {t("this_product_belongs_to_multiple_relations")}
            </Subheading>
            <Box
                sx={{
                    mt:1.5,
                    display:"flex",
                    flexDirection:"column",
                }}
            >
                <Text>
                    {t("put_this_with")}
                </Text>
                <Text bold>
                    {returnLineToProcess.product?.relation?.name}
                </Text>
            </Box>
            {/* {console.log(returnLineToProcess.product?.relation?.name)} */}
        </Box>
    )
}